import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, Send, Paperclip, X, Reply, Pencil, Trash2, Smile, ChevronDown, Check, CheckCheck, FileText, Image as ImageIcon, Settings, Loader2, AlertCircle, RotateCcw, Download, Film, File, ExternalLink, Play, Phone, Info, Eye, Pin, PinOff, BellOff, Plus, Inbox, Repeat, ClipboardList, Wrench, BarChart3, CheckSquare, PartyPopper, Camera } from 'lucide-react';
import { SkloKruh, SkloPilulka } from './ui/Sklo';
import { getAvatarBarva } from '../lib/avatar';
import { ROZOSTRENI_NAVIGACE } from '../lib/uiTokens';
import { KAPSLE_LISTY } from './MobilniLista';
import ModalShell, { Z_NAD_LISTOU } from './ui/ModalShell';
import useJeMobil from '../hooks/useJeMobil';
import { SECTION_COLOR, SECTION_BG, SECTION_BORDER, hlavicky } from './komunikace/komunikace';
import ZaskokKarta from './komunikace/ZaskokKarta';
import NovaZadost from './komunikace/zadosti/NovaZadost';
import NovyUkol from './komunikace/ukoly/NovyUkol';
import UkolKarta from './komunikace/ukoly/UkolKarta';
import AnketaKarta from './komunikace/AnketaKarta';
import NovaAnketa from './komunikace/NovaAnketa';
import Podekovat from './komunikace/Podekovat';
import { pripravitSoubor, PRIJIMANE } from './komunikace/prilohy/prilohy';

const EMOJI_LIST = ['👍', '❤️', '😂', '😮', '😢', '🔥', '👏', '🎉'];
// „Píše…" se posílá nejvýš jednou za tuto dobu — dřív s každým úhozem
const PISE_INTERVAL_MS = 2500;

function ChatAvatar({ url, jmeno, size = 36 }) {
    const inicialy = jmeno
        ? jmeno.split(' ').filter(Boolean).map(w => w[0]?.toUpperCase()).slice(0, 2).join('')
        : '?';

    return url ? (
        <img src={url} alt={jmeno} className="rounded-full object-cover shrink-0"
            style={{ width: size, height: size }} />
    ) : (
        <div className="rounded-full flex items-center justify-center text-white font-bold shrink-0"
            style={{ width: size, height: size, background: getAvatarBarva(jmeno), fontSize: size * 0.35 }}>
            {inicialy}
        </div>
    );
}

function formatCas(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    const now = new Date();
    const diff = (now - d) / 1000;
    if (diff < 60) return 'právě teď';
    if (diff < 3600) return `${Math.floor(diff / 60)} min`;
    if (diff < 86400 && d.getDate() === now.getDate()) return d.toLocaleTimeString('cs', { hour: '2-digit', minute: '2-digit' });
    return d.toLocaleDateString('cs', { day: 'numeric', month: 'numeric' }) + ' ' + d.toLocaleTimeString('cs', { hour: '2-digit', minute: '2-digit' });
}

function formatVelikost(b) {
    if (b < 1024) return `${b} B`;
    if (b < 1048576) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / 1048576).toFixed(1)} MB`;
}

export function renderFormattedText(text) {
    if (!text) return null;
    // Rozdělit na řádky a zpracovat bloky
    const lines = text.split('\n');
    const elements = [];
    let key = 0;

    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];

        // Oddělovač ---
        if (/^-{3,}$/.test(line.trim())) {
            elements.push(<hr key={key++} style={{ border: 'none', borderTop: '1px solid rgba(128,128,128,0.3)', margin: '8px 0' }} />);
            continue;
        }

        // Nadpis ## nebo #
        const headingMatch = line.match(/^(#{1,3})\s+(.+)$/);
        if (headingMatch) {
            const level = headingMatch[1].length;
            const sizes = { 1: '1.2em', 2: '1.05em', 3: '0.95em' };
            elements.push(<div key={key++} style={{ fontWeight: 700, fontSize: sizes[level], marginTop: i > 0 ? 6 : 0, marginBottom: 2 }}>{formatInline(headingMatch[2])}</div>);
            continue;
        }

        // Seznam - nebo •
        const listMatch = line.match(/^[\-\u2022\*]\s+(.+)$/);
        if (listMatch) {
            elements.push(<div key={key++} style={{ paddingLeft: 12, display: 'flex', gap: 6 }}><span style={{ opacity: 0.5 }}>•</span><span>{formatInline(listMatch[1])}</span></div>);
            continue;
        }

        // Číslovaný seznam
        const numMatch = line.match(/^(\d+)[.)]\s+(.+)$/);
        if (numMatch) {
            elements.push(<div key={key++} style={{ paddingLeft: 12, display: 'flex', gap: 6 }}><span style={{ opacity: 0.5 }}>{numMatch[1]}.</span><span>{formatInline(numMatch[2])}</span></div>);
            continue;
        }

        // Prázdný řádek
        if (!line.trim()) {
            elements.push(<div key={key++} style={{ height: 6 }} />);
            continue;
        }

        // Normální řádek s inline formátováním
        elements.push(<div key={key++}>{formatInline(line)}</div>);
    }
    return elements;
}

function formatInline(text) {
    // Inline formátování: **bold**, *italic*, ~~strike~~, `code`
    const parts = [];
    let remaining = text;
    let key = 0;

    const patterns = [
        { re: /\*\*(.+?)\*\*/, render: (m) => <strong key={key++}>{m}</strong> },
        { re: /\*(.+?)\*/, render: (m) => <em key={key++}>{m}</em> },
        { re: /~~(.+?)~~/, render: (m) => <s key={key++} style={{ opacity: 0.6 }}>{m}</s> },
        { re: /`(.+?)`/, render: (m) => <code key={key++} style={{ background: 'rgba(128,128,128,0.2)', borderRadius: 3, padding: '1px 5px', fontSize: '0.9em' }}>{m}</code> },
    ];

    while (remaining.length > 0) {
        let earliest = null;
        let earliestIdx = remaining.length;
        let earliestPattern = null;

        for (const p of patterns) {
            const match = remaining.match(p.re);
            if (match && match.index < earliestIdx) {
                earliest = match;
                earliestIdx = match.index;
                earliestPattern = p;
            }
        }

        if (!earliest) {
            parts.push(remaining);
            break;
        }

        if (earliestIdx > 0) {
            parts.push(remaining.substring(0, earliestIdx));
        }
        parts.push(earliestPattern.render(earliest[1]));
        remaining = remaining.substring(earliestIdx + earliest[0].length);
    }

    return parts.length === 1 && typeof parts[0] === 'string' ? parts[0] : parts;
}

// Ikona souboru podle MIME typu — neutrálně; barvy značek (modré Wordy,
// červená PDF) by v chatu dělaly semafor, který nic neznamená.
function fileIcon(mime) {
    if (mime?.startsWith('application/pdf') || mime?.includes('word') || mime?.includes('document')
        || mime?.includes('sheet') || mime?.includes('presentation') || mime?.startsWith('text/')) {
        return <FileText size={22} style={{ color: 'var(--text-secondary)' }} />;
    }
    return <File size={22} style={{ color: 'var(--text-secondary)' }} />;
}

// Detekce a formátování URL v textu
const URL_RE = /(https?:\/\/[^\s<>"')\]]+)/g;

// `@Jméno Příjmení` účastníka zvýrazněný v textu zprávy
function zvyrazniZminky(text, jmena, klicZaklad) {
    if (!jmena?.length || !text.includes('@')) return text;
    const escape = (t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const re = new RegExp('(@(?:' + jmena.map(escape).join('|') + '))', 'gi');
    const kusy = text.split(re);
    if (kusy.length === 1) return text;
    return kusy.map((k, i) => (i % 2 === 1
        ? <span key={klicZaklad + '-' + i} style={{ color: SECTION_COLOR, fontWeight: 600 }}>{k}</span>
        : k));
}

function renderTextWithLinks(text, isOwn, jmena = []) {
    if (!text) return null;
    const parts = text.split(URL_RE);
    if (parts.length === 1) return zvyrazniZminky(text, jmena, 't');
    return parts.map((part, i) => {
        if (URL_RE.test(part)) {
            URL_RE.lastIndex = 0;
            let hostname;
            try { hostname = new URL(part).hostname; } catch { hostname = part; }
            return (
                <a key={i} href={part} target="_blank" rel="noopener noreferrer"
                    onClick={e => e.stopPropagation()}
                    style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        color: SECTION_COLOR,
                        textDecoration: 'underline', textUnderlineOffset: 2,
                        wordBreak: 'break-all',
                    }}>
                    <ExternalLink size={12} style={{ flexShrink: 0 }} />
                    {hostname}
                </a>
            );
        }
        return <span key={i}>{zvyrazniZminky(part, jmena, 'p' + i)}</span>;
    });
}

/**
 * Okno konverzace. Používá ho Komunikace (detail vedle seznamu, na mobilu
 * přes celou obrazovku) i MiniChat.
 *
 * @param {Function} [onInfo] - ⓘ v hlavičce: „O konverzaci" tam, kde není třetí sloupec
 * @param {Function} [onNastaveni] - ozubené kolo: nastavení konverzace (modal drží rodič)
 * @param {Function} [onDetail] - dostane detail konverzace po načtení (pro třetí sloupec)
 * @param {number} [obnovaDetailu] - změna čísla = znovu načíst detail (po úpravě v nastavení)
 */
/**
 * `compact` — malé plovoucí okno na počítači (MiniChat): hlavička jedním
 * řádkem s akcemi okna (`akceOkna`, zvětšit/zavřít), bez druhé hlavičky nad
 * sebou, plocha `--surface-raised` (okno leží nad stránkou) a nejmenší psací
 * pole. Na počítači je psací pole menší i na celé stránce — mobilní kapsle
 * (64 px, 16px písmo kvůli zoomu v iOS) tam jen zabírala místo.
 */
export default function ChatWindow({ konverzaceId, user, isDark, isOnline, onBack, onKonverzaceZmenena, onInfo, onNastaveni, onDetail, obnovaDetailu = 0, compact = false, akceOkna = null }) {
    const jeMobil = useJeMobil();
    const plocha = compact ? 'var(--surface-raised)' : 'var(--surface-base)';
    // Rozměry psacího pole: mobil = kapsle lišty, počítač menší, okno nejmenší
    const pole = compact
        ? { vyska: 42, tlacitko: 34, ikona: 16, okraj: 8, zdola: 8, pismo: 14 }
        : !jeMobil
            ? { vyska: 48, tlacitko: 36, ikona: 17, okraj: 12, zdola: 12, pismo: 14 }
            // Telefon: menší než kapsle lišty (64 px), písmo zůstává 16 — pod ním iOS při psaní přiblíží stránku
            : { vyska: 50, tlacitko: 40, ikona: 18, okraj: KAPSLE_LISTY.odstranBocni, zdola: KAPSLE_LISTY.odsazeniZdola, pismo: 16 };
    // Je seznam zpráv dole? Když psací pole naroste (někdo píše, citace,
    // přílohy, víc řádků), seznam se doroluje — jinak se poslední zpráva
    // schovala pod pole.
    const uKonceRef = useRef(true);
    const [detail, setDetail] = useState(null);
    const [zpravy, setZpravy] = useState([]);
    const [hasMore, setHasMore] = useState(false);
    const [text, setText] = useState('');
    const [replyTo, setReplyTo] = useState(null);
    const [editingId, setEditingId] = useState(null);
    const [editText, setEditText] = useState('');
    const [hoveredId, setHoveredId] = useState(null);
    // Floating popups: { zpravaId, x, y }
    const [menuPopup, setMenuPopup] = useState(null);
    const [emojiPopup, setEmojiPopup] = useState(null);
    const [precteniZpravy, setPrecteniZpravy] = useState(null);   // zpráva, u které se ukazuje kdo četl
    const [obnovaInterni, setObnovaInterni] = useState(0);         // znovu načíst detail (připnutí z Reverbu)
    const [zminka, setZminka] = useState(null);                    // rozepsané „@xy" → nabídka účastníků
    const [obnovaObjektu, setObnovaObjektu] = useState({});        // zpráva s kartou → čítač obnovy
    const [menuPridat, setMenuPridat] = useState(false);           // „+" v psacím poli
    const [novaZadost, setNovaZadost] = useState(false);
    const [mojeSmeny, setMojeSmeny] = useState(null);              // výběr směny pro „Hledám záskok"
    const [predavka, setPredavka] = useState(null);                // text předávky (null = zavřeno)
    const [hlaskaPridat, setHlaskaPridat] = useState(null);
    const [noveHlaseni, setNoveHlaseni] = useState(false);
    const [novaAnketa, setNovaAnketa] = useState(false);
    const [ukolZeZpravy, setUkolZeZpravy] = useState(null);         // zpráva → „Udělat úkol"
    const [podekovat, setPodekovat] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [pendingSoubory, setPendingSoubory] = useState([]);
    const [typingUsers, setTypingUsers] = useState([]);
    const [firstUnreadId, setFirstUnreadId] = useState(null);
    const [lightbox, setLightbox] = useState(null); // { url, nazev }
    const typingTimeouts = useRef({});
    const messagesEndRef = useRef(null);
    // Psací pole plave nad zprávami, takže si scroll musí nechat místo pod
    // obsahem — jinak poslední zpráva skončí pod kapslí a nejde k ní dorolovat.
    // Výška se měří, protože kapsle roste s délkou rozepsané zprávy; pevné
    // číslo by při víceřádkovém textu zase nesedělo.
    const psaciRef = useRef(null);
    const [vyskaPsani, setVyskaPsani] = useState(0);
    const messagesTopRef = useRef(null);
    const containerRef = useRef(null);
    const textareaRef = useRef(null);
    const fileInputRef = useRef(null);
    const fotakRef = useRef(null);
    const posledniPiseRef = useRef(0);
    const markReadRef = useRef(null);
    const precistCasovacRef = useRef(null);

    const isSdeleni = detail?.typ === 'sdeleni';
    const canWrite = !isSdeleni || detail?.ucastnici?.find(u => u.id === user?.id)?.role === 'admin';

    // iOS keyboard — visualViewport offset
    const [kbOffset, setKbOffset] = useState(0);
    useEffect(() => {
        if (!window.visualViewport) return;
        const handleResize = () => {
            const offset = window.innerHeight - window.visualViewport.height;
            // Přidat prostor pro iOS form accessory bar (~44px)
            const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
            setKbOffset(offset > 50 ? offset + (isIOS ? 44 : 0) : 0);
            // Scroll zprávy dolů při otevření klávesnice
            if (offset > 50) {
                setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'auto' }), 50);
            }
        };
        window.visualViewport.addEventListener('resize', handleResize);
        window.visualViewport.addEventListener('scroll', handleResize);
        return () => {
            window.visualViewport.removeEventListener('resize', handleResize);
            window.visualViewport.removeEventListener('scroll', handleResize);
        };
    }, []);

    // Reset při změně konverzace
    useEffect(() => {
        setZpravy([]);
        setDetail(null);
        setHasMore(false);
        setReplyTo(null);
        setEditingId(null);
        setText('');
        setIsSending(false);
        setPendingSoubory([]);
        setFirstUnreadId(null);
        setMenuPopup(null);
        setEmojiPopup(null);
        setPrecteniZpravy(null);
    }, [konverzaceId]);

    // Zavřít popupy při scrollu
    const handleScroll = () => {
        const el = containerRef.current;
        if (el) uKonceRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
        setMenuPopup(null);
        setEmojiPopup(null);
    };

    useEffect(() => {
        if (!konverzaceId) return;
        fetch(`/api/chat/konverzace/${konverzaceId}`).then(r => (r.ok ? r.json() : null)).then(d => {
            if (!d) return;
            setDetail(d);
            onDetail?.(d);
            if (!obnovaDetailu) setTimeout(() => textareaRef.current?.focus(), 100);
        }).catch(() => {});
    }, [konverzaceId, obnovaDetailu, obnovaInterni]); // eslint-disable-line react-hooks/exhaustive-deps

    const markDelivered = useCallback(() => {
        if (!konverzaceId) return;
        fetch(`/api/chat/konverzace/${konverzaceId}/doruceno`, {
            method: 'POST',
            headers: {
                'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content,
                'X-Socket-ID': window.Echo?.socketId() ?? '',
            },
        }).catch(() => {});
    }, [konverzaceId]);

    const fetchZpravy = useCallback((beforeId = null) => {
        if (!konverzaceId) return;
        const url = beforeId
            ? `/api/chat/konverzace/${konverzaceId}/zpravy?before_id=${beforeId}`
            : `/api/chat/konverzace/${konverzaceId}/zpravy`;
        fetch(url).then(r => r.json()).then(data => {
            if (beforeId) {
                setZpravy(prev => [...data.data, ...prev]);
            } else {
                setZpravy(data.data);
                // Scroll na konec — dvakrát pro jistotu (render může trvat)
                setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'auto' }), 50);
                setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'auto' }), 300);
                // Karty (záskok, úkol, anketa) si data načítají samy a mění výšku — ještě jednou dolů
                setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'auto' }), 900);
                markDelivered();
            }
            setHasMore(data.has_more);
        });
    }, [konverzaceId, markDelivered]);

    useEffect(() => { fetchZpravy(); }, [fetchZpravy]);

    // Refetch zpráv po návratu z pozadí (probuzení telefonu)
    useEffect(() => {
        const handleResumed = () => { fetchZpravy(); };
        window.addEventListener('app-resumed', handleResumed);
        return () => window.removeEventListener('app-resumed', handleResumed);
    }, [fetchZpravy]);

    const markRead = useCallback(() => {
        if (!konverzaceId) return;
        setFirstUnreadId(null);
        fetch(`/api/chat/konverzace/${konverzaceId}/precist`, {
            method: 'POST',
            headers: {
                'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content,
                'X-Socket-ID': window.Echo?.socketId() ?? '',
            },
        }).then(() => {
            // Refresh badge počtu po přečtení
            window.dispatchEvent(new Event('chat-nova-zprava'));
        });
    }, [konverzaceId]);

    markReadRef.current = markRead;

    useEffect(() => {
        if (!konverzaceId) return;
        markRead();
    }, [konverzaceId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Nová zpráva v otevřené konverzaci: když je okno vidět, rovnou přečteno
    // (dřív jen „doručeno" — odznak rostl, i když člověk chat právě četl).

    useEffect(() => {
        if (!konverzaceId || !window.Echo) return;
        const ch = window.Echo.private(`konverzace.${konverzaceId}`);

        const naZpravu = e => {
            setZpravy(prev => {
                // Pokud zpráva už existuje (optimisticky vložená a nahrazená), neopakovat
                if (prev.some(z => z.id === e.zprava.id)) return prev;
                return [...prev, e.zprava];
            });
            setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
            if (e.zprava?.odesilatel_id !== user?.id) {
                if (document.visibilityState === 'visible') {
                    clearTimeout(precistCasovacRef.current);
                    precistCasovacRef.current = setTimeout(() => markReadRef.current?.(), 600);
                } else {
                    setFirstUnreadId(prev => prev ?? e.zprava.id);
                    markDelivered();
                }
                try {
                    const ctx = new (window.AudioContext || window.webkitAudioContext)();
                    const osc = ctx.createOscillator();
                    const gain = ctx.createGain();
                    osc.connect(gain);
                    gain.connect(ctx.destination);
                    osc.frequency.value = 880;
                    osc.type = 'sine';
                    gain.gain.setValueAtTime(0.08, ctx.currentTime);
                    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.25);
                    osc.start(ctx.currentTime);
                    osc.stop(ctx.currentTime + 0.25);
                } catch (_) {}
            }
            onKonverzaceZmenena?.();
        };
        const naUpravu = e => setZpravy(prev => prev.map(z => z.id === e.zprava_id ? { ...z, obsah: e.obsah, upraveno: true } : z));
        const naSmazani = e => setZpravy(prev => prev.map(z => z.id === e.zprava_id ? { ...z, smazano: true, obsah: null } : z));
        const naReakci = e => setZpravy(prev => prev.map(z => z.id === e.zprava_id ? { ...z, reakce: e.reakce } : z));
        const naPrecteni = e => {
            if (!e.zprava_ids?.length) return;
            const idsSet = new Set(e.zprava_ids.map(Number));
            setZpravy(prev => prev.map(z => (idsSet.has(z.id) && !(z.precteno_kym || []).includes(Number(e.uzivatel_id)))
                ? { ...z, precteno_kym: [...(z.precteno_kym || []), Number(e.uzivatel_id)] } : z));
        };
        const naDoruceni = e => {
            if (!e.zprava_ids?.length) return;
            const idsSet = new Set(e.zprava_ids.map(Number));
            setZpravy(prev => prev.map(z => (idsSet.has(z.id) && !(z.doruceno_kym || []).includes(Number(e.uzivatel_id)))
                ? { ...z, doruceno_kym: [...(z.doruceno_kym || []), Number(e.uzivatel_id)] } : z));
        };
        const naPsani = (e) => {
            if (e.id === user?.id) return;
            setTypingUsers(prev => prev.some(u => u.id === e.id) ? prev : [...prev, { id: e.id, jmeno: e.jmeno }]);
            clearTimeout(typingTimeouts.current[e.id]);
            typingTimeouts.current[e.id] = setTimeout(() => {
                setTypingUsers(prev => prev.filter(u => u.id !== e.id));
            }, 3500);
        };

        ch.listen('.NovaChatZprava', naZpravu);
        ch.listen('.ChatZpravaUpravena', naUpravu);
        ch.listen('.ChatZpravaSmazana', naSmazani);
        ch.listen('.ChatReakceZmenena', naReakci);
        ch.listen('.ChatPrectenoAktualizovano', naPrecteni);
        ch.listen('.ChatDorucenoAktualizovano', naDoruceni);
        const naPripnuti = e => {
            setZpravy(prev => prev.map(z => z.id === e.zprava_id ? { ...z, pripnuto: !!e.pripnuto } : z));
            setObnovaInterni(n => n + 1);
        };
        ch.listen('.ChatPripnutiZmeneno', naPripnuti);
        const naObjekt = e => setObnovaObjektu(prev => ({ ...prev, [e.zprava_id]: (prev[e.zprava_id] || 0) + 1 }));
        ch.listen('.ChatObjektZmenen', naObjekt);
        ch.listenForWhisper('typing', naPsani);
        return () => {
            // Jen vlastní posluchače — `Echo.leave` by odpojil i jiné okno
            // téže konverzace (MiniChat + stránka, přemontování).
            ch.stopListening('.NovaChatZprava', naZpravu);
            ch.stopListening('.ChatZpravaUpravena', naUpravu);
            ch.stopListening('.ChatZpravaSmazana', naSmazani);
            ch.stopListening('.ChatReakceZmenena', naReakci);
            ch.stopListening('.ChatPrectenoAktualizovano', naPrecteni);
            ch.stopListening('.ChatDorucenoAktualizovano', naDoruceni);
            ch.stopListening('.ChatPripnutiZmeneno', naPripnuti);
            ch.stopListening('.ChatObjektZmenen', naObjekt);
            ch.stopListeningForWhisper('typing', naPsani);
            clearTimeout(precistCasovacRef.current);
            setTypingUsers([]);
        };
    }, [konverzaceId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!hasMore || !messagesTopRef.current) return;
        const obs = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && zpravy.length > 0) {
                fetchZpravy(zpravy[0].id);
            }
        }, { threshold: 0.1 });
        obs.observe(messagesTopRef.current);
        return () => obs.disconnect();
    }, [hasMore, zpravy, fetchZpravy]);

    const handleSend = async () => {
        const trimmed = text.trim();
        if (!trimmed && pendingSoubory.length === 0) return;
        if (isSending) return;
        setIsSending(true);

        const tempId = `temp-${Date.now()}`;
        const savedText = trimmed;
        const savedReplyTo = replyTo;
        const savedSoubory = [...pendingSoubory];

        // Optimistická zpráva — zobrazí se okamžitě
        const optimistic = {
            id: tempId,
            _optimistic: true,
            konverzace_id: konverzaceId,
            odesilatel_id: user?.id,
            odesilatel: { id: user?.id, jmeno: user?.jmeno, avatar_url: user?.avatar_url },
            obsah: savedText || null,
            typ: savedSoubory.length > 0 ? 'soubor' : 'text',
            soubory: savedSoubory,
            odpoved_na: savedReplyTo ? { obsah: savedReplyTo.obsah, autor: savedReplyTo.odesilatel?.jmeno } : null,
            reakce: [],
            created_at: new Date().toISOString(),
        };

        setZpravy(prev => [...prev, optimistic]);
        setText('');
        setReplyTo(null);
        setPendingSoubory([]);
        if (textareaRef.current) textareaRef.current.style.height = 'auto';
        setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);

        try {
            const res = await fetch(`/api/chat/konverzace/${konverzaceId}/zpravy`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content,
                    'X-Socket-ID': window.Echo?.socketId() ?? '',
                },
                body: JSON.stringify({
                    obsah: savedText || null,
                    odpoved_na_id: savedReplyTo?.id || null,
                    soubor_ids: savedSoubory.map(s => s.id),
                }),
            });
            if (res.ok) {
                const zprava = await res.json();
                // Nahradit optimistickou zprávu reálnou ze serveru
                setZpravy(prev => prev.map(z => z.id === tempId ? zprava : z));
            } else {
                // Označit zprávu jako neúspěšnou
                setZpravy(prev => prev.map(z => z.id === tempId ? { ...z, _failed: true, _savedBody: { obsah: savedText, odpoved_na_id: savedReplyTo?.id || null, soubor_ids: savedSoubory.map(s => s.id) } } : z));
            }
        } catch {
            setZpravy(prev => prev.map(z => z.id === tempId ? { ...z, _failed: true, _savedBody: { obsah: savedText, odpoved_na_id: savedReplyTo?.id || null, soubor_ids: savedSoubory.map(s => s.id) } } : z));
        } finally {
            setIsSending(false);
        }
    };

    const handleRetry = async (failedMsg) => {
        const tempId = failedMsg.id;
        // Reset stavu na odesílání
        setZpravy(prev => prev.map(z => z.id === tempId ? { ...z, _failed: false } : z));
        try {
            const res = await fetch(`/api/chat/konverzace/${konverzaceId}/zpravy`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content,
                    'X-Socket-ID': window.Echo?.socketId() ?? '',
                },
                body: JSON.stringify(failedMsg._savedBody),
            });
            if (res.ok) {
                const zprava = await res.json();
                setZpravy(prev => prev.map(z => z.id === tempId ? zprava : z));
            } else {
                setZpravy(prev => prev.map(z => z.id === tempId ? { ...z, _failed: true } : z));
            }
        } catch {
            setZpravy(prev => prev.map(z => z.id === tempId ? { ...z, _failed: true } : z));
        }
    };

    const handleKeyDown = (e) => {
        if (zminka !== null && nabidkaZminek.length > 0 && (e.key === 'Enter' || e.key === 'Tab')) {
            e.preventDefault();
            vlozZminku(nabidkaZminek[0]);
            return;
        }
        if (e.key === 'Escape' && zminka !== null) { setZminka(null); return; }
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            if (!isSending) handleSend();
        }
    };

    const handleEdit = async (id) => {
        const res = await fetch(`/api/chat/zpravy/${id}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content },
            body: JSON.stringify({ obsah: editText }),
        });
        if (res.ok) {
            setZpravy(prev => prev.map(z => z.id === id ? { ...z, obsah: editText, upraveno: true } : z));
            setEditingId(null);
        }
    };

    const handleDelete = async (id) => {
        const res = await fetch(`/api/chat/zpravy/${id}`, {
            method: 'DELETE',
            headers: { 'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content },
        });
        if (res.ok) {
            setZpravy(prev => prev.map(z => z.id === id ? { ...z, smazano: true, obsah: null } : z));
        }
    };

    const jsemSpravce = detail?.ucastnici?.find(u => u.id === user?.id)?.role === 'admin';
    const smiPripnout = !!detail && (!['kanal', 'sdeleni'].includes(detail.typ) || jsemSpravce);
    const handlePripnout = async (zpravaId) => {
        const res = await fetch(`/api/chat/zpravy/${zpravaId}/pripnout`, {
            method: 'POST',
            headers: { 'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content, 'X-Socket-ID': window.Echo?.socketId() ?? '' },
        });
        if (res.ok) {
            const data = await res.json();
            setZpravy(prev => prev.map(z => z.id === zpravaId ? { ...z, pripnuto: data.pripnuto } : z));
            setDetail(prev => (prev ? { ...prev, pripnute: data.pripnute } : prev));
        }
    };

    // Jména účastníků pro zmínky (zvýraznění a nabídka při psaní „@")
    const jmenaUcastniku = (detail?.ucastnici || []).map(u => u.jmeno).filter(Boolean);
    const nabidkaZminek = zminka === null ? [] : (detail?.ucastnici || [])
        .filter(u => u.id !== user?.id && (u.jmeno || '').toLowerCase().includes(zminka.toLowerCase()))
        .slice(0, 6);
    const vlozZminku = (u) => {
        const el = textareaRef.current;
        const konec = el ? el.selectionStart : text.length;
        const pred = text.slice(0, konec).replace(/@[^\s@]*$/, '@' + u.jmeno + ' ');
        const novy = pred + text.slice(konec);
        setText(novy);
        setZminka(null);
        setTimeout(() => { if (el) { el.focus(); el.setSelectionRange(pred.length, pred.length); } }, 0);
    };

    const handleReakce = async (zpravaId, emoji) => {
        const res = await fetch(`/api/chat/zpravy/${zpravaId}/reakce`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content },
            body: JSON.stringify({ emoji }),
        });
        if (res.ok) {
            const data = await res.json();
            setZpravy(prev => prev.map(z => z.id === zpravaId ? { ...z, reakce: data.reakce } : z));
        }
    };

    // Víc souborů najednou (galerie), fotky zmenšené a HEIC převedené na JPEG —
    // telefon fotí 5 MB snímky, které se do 10 MB limitu chatu nevešly
    // a v prohlížečích mimo Safari se nezobrazily.
    const handleFileSelect = async (e) => {
        const files = [...(e.target.files || [])].slice(0, 10);
        e.target.value = '';
        if (!files.length) return;
        setUploading(true);
        for (const puvodni of files) {
            const file = await pripravitSoubor(puvodni);
            const fd = new FormData();
            fd.append('soubor', file);
            fd.append('konverzace_id', konverzaceId);
            try {
                const res = await fetch('/api/chat/soubory', {
                    method: 'POST',
                    headers: { 'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content, Accept: 'application/json' },
                    body: fd,
                });
                if (res.ok) {
                    const data = await res.json();
                    setPendingSoubory(prev => [...prev, data]);
                }
            } catch { /* další soubor */ }
        }
        setUploading(false);
    };

    // Otevřít emoji picker — anchorovat přímo na pozici kurzoru
    const openEmojiPicker = (e, zpravaId) => {
        e.stopPropagation();
        const x = e.clientX;
        let y = e.clientY - 60; // nad kurzorem
        if (y < 8) y = e.clientY + 12; // flipnout pod kurzor, pokud není místo
        setEmojiPopup(prev => prev?.zpravaId === zpravaId ? null : { zpravaId, x, y });
        setMenuPopup(null);
    };

    // Otevřít context menu na fixed pozici
    const openMenu = (e, zpravaId, isOwn) => {
        e.stopPropagation();
        const rect = e.currentTarget.getBoundingClientRect();
        const W = 160;
        let x = isOwn ? rect.right - W : rect.left;
        let y = rect.bottom + 4;
        if (x < 8) x = 8;
        if (x + W > window.innerWidth - 8) x = window.innerWidth - W - 8;
        if (y + 120 > window.innerHeight - 8) y = rect.top - 124;
        setMenuPopup(prev => prev?.zpravaId === zpravaId ? null : { zpravaId, x, y, isOwn });
        setEmojiPopup(null);
    };

    useEffect(() => {
        const el = psaciRef.current;
        if (!el) { setVyskaPsani(0); return; }
        const zmer = () => setVyskaPsani(el.offsetHeight);
        zmer();
        const ro = new ResizeObserver(zmer);
        ro.observe(el);
        return () => ro.disconnect();
    }, [canWrite]);
    useEffect(() => {
        const el = containerRef.current;
        if (el && uKonceRef.current) el.scrollTop = el.scrollHeight;
    }, [vyskaPsani]);

    const closeAllPopups = () => {
        setMenuPopup(null);
        setEmojiPopup(null);
    };

    // Popup style — glassmorphism
    const popupStyle = {
        background: isDark ? 'rgba(24,22,32,0.96)' : 'white',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)'}`,
        boxShadow: '0 8px 32px rgba(0,0,0,0.35)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
    };

    const bubbleOwn = {
        background: isDark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.06)',
        border: isDark ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.08)',
        boxShadow: `inset -2px 0 0 ${SECTION_COLOR}`,
        borderRadius: '18px 18px 4px 18px',
        color: 'var(--text-primary)',
        padding: '10px 14px',
        wordBreak: 'break-word',
    };
    const bubbleOther = {
        background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)',
        border: isDark ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.08)',
        borderRadius: '18px 18px 18px 4px',
        color: 'var(--text-primary)',
        padding: '10px 14px',
        wordBreak: 'break-word',
    };
    const bubbleSdeleni = {
        background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
        border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.06)',
        borderRadius: '12px',
        color: 'var(--text-primary)',
        padding: '14px 18px',
        wordBreak: 'break-word',
    };
    const systemStyle = { textAlign: 'center', color: 'var(--text-muted)', fontSize: '12px', fontStyle: 'italic', padding: '8px 0' };

    if (!konverzaceId) {
        return (
            <div className="flex-1 flex items-center justify-center" style={{ color: 'var(--text-muted)' }}>
                <div className="text-center">
                    <p className="text-lg font-medium mb-1">Vyberte konverzaci</p>
                    <p className="text-sm">nebo zahajte nový chat</p>
                </div>
            </div>
        );
    }

    const menuZprava = menuPopup ? zpravy.find(z => z.id === menuPopup.zpravaId) : null;

    // Doručení a přečtení. Ikona u každé vlastní zprávy (✓ odesláno, ✓✓ doručeno),
    // u **poslední** vlastní zprávy navíc čitelný řádek „Přečteno" / „přečetlo
    // 9 z 12" — klepnutí ukáže kdo ano a kdo ne. Dřív jen tooltip na najetí
    // myší, na dotyku nepoužitelný, a modrá fajfka mimo paletu.
    const posledniVlastniId = (() => {
        for (let i = zpravy.length - 1; i >= 0; i--) {
            const z = zpravy[i];
            if (z.odesilatel_id === user?.id && !z.smazano && !z._optimistic && z.typ !== 'system') return z.id;
        }
        return null;
    })();
    const stavDoruceni = (z) => {
        const ostatni = (detail?.ucastnici || []).filter(u => u.id !== z.odesilatel_id);
        const precteno = (z.precteno_kym || []).filter(id => ostatni.some(u => u.id === id));
        const doruceno = z.doruceno_kym || [];
        const vsichni = ostatni.length > 0 && precteno.length >= ostatni.length;
        const sedaIkona = isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.35)';
        const ikona = precteno.length > 0 || doruceno.length > 0
            ? <CheckCheck size={12} style={{ color: vsichni ? SECTION_COLOR : sedaIkona }} />
            : <Check size={12} style={{ color: sedaIkona }} />;
        if (z.id !== posledniVlastniId || ostatni.length === 0) return ikona;
        const text = detail?.typ === 'soukromy'
            ? (precteno.length ? 'Přečteno' : doruceno.length ? 'Doručeno' : 'Odesláno')
            : (vsichni ? 'Přečetli všichni' : `přečetlo ${precteno.length} z ${ostatni.length}`);
        return (
            <button type="button" onClick={(e) => { e.stopPropagation(); if (detail?.typ !== 'soukromy') setPrecteniZpravy(z); }}
                className="inline-flex items-center gap-1"
                style={{ background: 'none', border: 'none', padding: 0, color: 'inherit', font: 'inherit', cursor: detail?.typ !== 'soukromy' ? 'pointer' : 'default' }}>
                {ikona} {text}
            </button>
        );
    };

    // Portály renderují do document.body — obcházejí backdrop-filter containing block
    const emojiPortal = emojiPopup ? createPortal(
        <div onClick={e => e.stopPropagation()} style={{
            position: 'fixed', left: emojiPopup.x, top: emojiPopup.y,
            transform: 'translateX(-50%)', zIndex: Z_NAD_LISTOU + 20,
            borderRadius: '16px', padding: '8px', display: 'flex', gap: '2px',
            ...popupStyle,
        }}>
            {EMOJI_LIST.map(e => (
                <button key={e}
                    onClick={() => { handleReakce(emojiPopup.zpravaId, e); setEmojiPopup(null); }}
                    style={{ fontSize: '20px', padding: '6px', lineHeight: 1, borderRadius: '8px', transition: 'transform 0.1s' }}
                    onMouseEnter={el => el.currentTarget.style.transform = 'scale(1.3)'}
                    onMouseLeave={el => el.currentTarget.style.transform = 'scale(1)'}
                >{e}</button>
            ))}
        </div>,
        document.body
    ) : null;

    const menuPortal = menuPopup && menuZprava ? createPortal(
        <div onClick={e => e.stopPropagation()} style={{
            position: 'fixed', left: menuPopup.x, top: menuPopup.y, zIndex: Z_NAD_LISTOU + 20,
            borderRadius: '14px', padding: '4px 0', minWidth: '160px',
            ...popupStyle,
        }}>
            {!isSdeleni && canWrite && (
                <button onClick={() => { setReplyTo(menuZprava); setMenuPopup(null); }}
                    className="w-full flex items-center gap-2 text-xs px-4 py-2.5 text-left"
                    style={{ color: 'var(--text-primary)' }}
                    onMouseEnter={e => e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <Reply size={14} /> Odpovědět
                </button>
            )}
            {menuZprava.typ === 'text' && !menuZprava.smazano && detail?.typ !== 'sdeleni' && (
                <button onClick={() => { setUkolZeZpravy(menuZprava); setMenuPopup(null); }}
                    className="w-full flex items-center gap-2 text-xs px-4 py-2.5 text-left"
                    style={{ color: 'var(--text-primary)' }}
                    onMouseEnter={e => e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <CheckSquare size={14} /> Udělat úkol
                </button>
            )}
            {smiPripnout && menuZprava.typ !== 'system' && (
                <button onClick={() => { handlePripnout(menuZprava.id); setMenuPopup(null); }}
                    className="w-full flex items-center gap-2 text-xs px-4 py-2.5 text-left"
                    style={{ color: 'var(--text-primary)' }}
                    onMouseEnter={e => e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    {menuZprava.pripnuto ? <><PinOff size={14} /> Odepnout</> : <><Pin size={14} /> Připnout</>}
                </button>
            )}
            {menuZprava.odesilatel_id === user?.id && menuZprava.lze_upravit && (
                <button onClick={() => { setEditingId(menuZprava.id); setEditText(menuZprava.obsah); setMenuPopup(null); }}
                    className="w-full flex items-center gap-2 text-xs px-4 py-2.5 text-left"
                    style={{ color: 'var(--text-primary)' }}
                    onMouseEnter={e => e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <Pencil size={14} /> Upravit
                </button>
            )}
            {menuZprava.odesilatel_id === user?.id && (
                <button onClick={() => { handleDelete(menuZprava.id); setMenuPopup(null); }}
                    className="w-full flex items-center gap-2 text-xs px-4 py-2.5 text-left"
                    style={{ color: '#EF4444' }}
                    onMouseEnter={e => e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <Trash2 size={14} /> Smazat
                </button>
            )}
        </div>,
        document.body
    ) : null;

    return (
        <>
        <div className="flex-1 flex flex-col" style={{ minHeight: 0, position: 'relative' }}>
            {/* Header */}
            {/* Hlavička je krytá plochou aplikace a **u spodního okraje dozní
                do průhledna** (pás níž), takže se do ní obsah při listování
                ztrácí místo aby se o ni ostře usekl. Žádná linka — ta by byla
                tvrdý předěl přesně tam, kde má být přechod. */}
            <div className={compact ? 'flex items-center gap-2.5 px-2 py-2' : 'flex items-center gap-3 px-4 py-3'}
                style={{ flexShrink: 0, background: plocha, position: 'relative', zIndex: 2,
                    ...(compact ? { borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-subtle)'}` } : null) }}>
                {/* Mobil: návrat jako skleněný kruh (horní ovládání, `Sklo.jsx`); okno: tiché ‹ */}
                {onBack && compact && (
                    <button type="button" onClick={onBack} aria-label="Zpět na konverzace"
                        className="flex items-center justify-center rounded-lg shrink-0"
                        style={{ width: 30, height: 30, color: 'var(--text-secondary)', background: 'none', border: 'none', cursor: 'pointer' }}>
                        <ChevronLeft size={18} />
                    </button>
                )}
                {onBack && !compact && (
                    <SkloKruh Icon={ChevronLeft} ikona={22} popis="Zpět na konverzace" onClick={onBack} isDark={isDark} style={{ marginLeft: -4 }} />
                )}

                {detail?.typ === 'soukromy' ? (() => {
                    const druhy = detail.ucastnici?.find(u => u.id !== user?.id);
                    return <ChatAvatar url={druhy?.avatar_url} jmeno={druhy?.jmeno} size={compact ? 30 : 36} />;
                })() : (
                    <div className={`${compact ? 'w-[30px] h-[30px] text-xs' : 'w-9 h-9 text-sm'} rounded-full shrink-0 overflow-hidden flex items-center justify-center font-bold`}
                        style={{ background: detail?.avatar_url ? 'transparent' : (isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)'), color: 'var(--text-primary)' }}>
                        {detail?.avatar_url
                            ? <img src={detail.avatar_url} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                            : (detail?.ikona || detail?.nazev?.[0]?.toUpperCase() || '?')}
                    </div>
                )}

                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                        <span className="font-semibold text-sm truncate" style={{ color: 'var(--text-primary)' }}>
                            {detail?.nazev || detail?.ucastnici?.find(u => u.id !== user?.id)?.jmeno || 'Konverzace'}
                        </span>
                        {detail?.typ === 'soukromy' && isOnline && (
                            <span className="w-2 h-2 rounded-full shrink-0" style={{ background: '#10B981' }} />
                        )}
                    </div>
                    {detail?.typ !== 'soukromy' ? (
                        <div className="flex items-center gap-0.5 mt-0.5">
                            <div className="flex -space-x-1.5">
                                {detail?.ucastnici?.slice(0, 5).map(u => (
                                    <div key={u.id} title={u.jmeno}>
                                        <ChatAvatar url={u.avatar_url} jmeno={u.jmeno} size={18}  />
                                    </div>
                                ))}
                            </div>
                            {detail?.ucastnici?.length > 5 && (
                                <span className="text-[10px] ml-1" style={{ color: 'var(--text-muted)' }}>
                                    +{detail.ucastnici.length - 5}
                                </span>
                            )}
                            <span className="text-[10px] ml-1" style={{ color: 'var(--text-muted)' }}>
                                {detail?.typ === 'kanal' ? 'kanál · ' : ''}{detail?.ucastnici?.length || 0} členů
                            </span>
                            {detail?.ztlumeno && <BellOff size={11} style={{ color: 'var(--text-muted)', marginLeft: 4 }} aria-label="Ztlumeno" />}
                        </div>
                    ) : (
                        <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
                            {isOnline ? 'Online' : (detail?.ucastnici?.find(u => u.id !== user?.id)?.pozice || 'Soukromá konverzace')}
                        </div>
                    )}
                </div>

                {/* Okno: jen akce okna (zvětšit, zavřít) — nastavení a info jsou na celé stránce */}
                {compact ? akceOkna : onBack ? (
                    <SkloPilulka isDark={isDark} polozky={[
                        (() => {
                            if (detail?.typ !== 'soukromy') return null;
                            const druhy = detail.ucastnici?.find(u => u.id !== user?.id);
                            const tel = (druhy?.telefon || '').replace(/\s+/g, '');
                            return tel ? { id: 'volat', Icon: Phone, popis: 'Volat ' + (druhy?.jmeno || ''), href: 'tel:' + tel } : null;
                        })(),
                        onNastaveni && detail ? { id: 'nastaveni', Icon: Settings, popis: 'Nastavení konverzace', onClick: onNastaveni } : null,
                        onInfo && detail ? { id: 'info', Icon: Info, popis: 'O konverzaci', onClick: onInfo } : null,
                    ]} />
                ) : (<>
                {/* Volat — u soukromé konverzace, když má druhý telefon.
                    Chat dlouho telefon vůbec neznal, takže z něj nešlo nic než
                    psát; číslo teď nese detail konverzace. */}
                {detail?.typ === 'soukromy' && (() => {
                    const druhy = detail.ucastnici?.find(u => u.id !== user?.id);
                    const tel = (druhy?.telefon || '').replace(/\s+/g, '');
                    if (!tel) return null;
                    return (
                        <a href={'tel:' + tel} aria-label={'Volat ' + (druhy?.jmeno || '')}
                            className="p-1.5 rounded-lg transition-colors shrink-0"
                            style={{ color: 'var(--text-secondary)' }}>
                            <Phone size={18} />
                        </a>
                    );
                })()}

                {/* Nastavení (modal drží rodič) — u skupin a sdělení pro všechny
                    členy: správce upravuje, člen může aspoň opustit. */}
                {onNastaveni && detail && (
                    <button type="button" onClick={onNastaveni} aria-label="Nastavení konverzace"
                        className="p-1.5 rounded-lg transition-colors shrink-0" style={{ color: 'var(--text-secondary)' }}>
                        <Settings size={18} />
                    </button>
                )}
                {/* ⓘ — „O konverzaci" tam, kde se nevejde třetí sloupec (mobil, úzké okno) */}
                {onInfo && detail && (
                    <button type="button" onClick={onInfo} aria-label="O konverzaci"
                        className="p-1.5 rounded-lg transition-colors shrink-0" style={{ color: 'var(--text-secondary)' }}>
                        <Info size={18} />
                    </button>
                )}
                </>)}
            </div>

            {detail?.pripnute?.length > 0 && (
                <div className="flex items-center gap-2 px-4" style={{
                    minHeight: 36, flexShrink: 0, position: 'relative', zIndex: 2, fontSize: 12.5,
                    background: plocha, borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
                    color: 'var(--text-secondary)',
                }}>
                    <Pin size={13} style={{ color: SECTION_COLOR, flexShrink: 0 }} />
                    <span className="truncate flex-1 min-w-0">
                        <span style={{ color: 'var(--text-muted)' }}>Připnuto: </span>
                        <b style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{detail.pripnute[0].obsah}</b>
                    </span>
                    {detail.pripnute.length > 1 && <span className="shrink-0" style={{ color: 'var(--text-muted)' }}>+{detail.pripnute.length - 1}</span>}
                </div>
            )}

            {/* Výdech pod hlavičkou. Zprávy se pod ni schovávají ostrým řezem
                — vypadá to, jako by byla hlavička useknutá. Pás z barvy plochy
                doznívá do průhledna, takže obsah při listování mizí plynule.
                Nulová výška, aby nezabíral místo v toku; kreslí se přes první
                řádky zpráv. */}
            <div style={{ position: 'relative', height: 0, zIndex: 2 }} aria-hidden="true">
                <div style={{
                    position: 'absolute', top: 0, left: 0, right: 0, height: 28,
                    pointerEvents: 'none',
                    backgroundImage: 'linear-gradient(180deg,'
                        + ` ${plocha} 0,`
                        + ` color-mix(in srgb, ${plocha} 62%, transparent) 45%,`
                        + ` color-mix(in srgb, ${plocha} 22%, transparent) 75%,`
                        + ' transparent 100%)',
                }} />
            </div>

            {/* Messages */}
            <div ref={containerRef}
                className="flex-1 overflow-y-auto px-4 py-3"
                onScroll={handleScroll}
                onClick={closeAllPopups}
                style={{
                    minHeight: 0, display: 'flex', flexDirection: 'column', gap: '4px',
                    // Místo pro plovoucí psací pole patří DOVNITŘ scrollu (stejně
                    // jako rezerva pod lištou u modulů). Odsazením celé plochy by
                    // pod zprávami vznikl prázdný pruh a chat by nesahal ke hraně.
                    paddingBottom: vyskaPsani ? vyskaPsani + 8 : undefined,
                }}>
                <div ref={messagesTopRef} />
                {zpravy.map(z => {
                    const isOwn = z.odesilatel_id === user?.id;
                    if (z.typ === 'system') {
                        return <div key={z.id} style={systemStyle}>{z.obsah}</div>;
                    }
                    // Karta živého objektu (záskok) — autor nad kartou, bez bubliny
                    if (z.typ === 'objekt' && ['ukol', 'anketa'].includes(z.objekt?.typ)) {
                        return (
                            <div key={z.id} style={{ margin: '6px 0' }}>
                                <div className="text-xs mb-1 ml-1" style={{ color: 'var(--text-muted)' }}>{z.odesilatel?.jmeno} · {formatCas(z.created_at)}</div>
                                {z.objekt.typ === 'ukol'
                                    ? <UkolKarta id={z.objekt.id} obnova={obnovaObjektu[z.id] || 0} isDark={isDark} onZmena={() => fetchZpravy()} />
                                    : <AnketaKarta id={z.objekt.id} obnova={obnovaObjektu[z.id] || 0} isDark={isDark} />}
                            </div>
                        );
                    }
                    if (z.typ === 'objekt' && z.objekt?.typ === 'zaskok') {
                        return (
                            <div key={z.id} style={{ margin: '6px 0' }}>
                                <div className="text-xs mb-1 ml-1" style={{ color: 'var(--text-muted)' }}>{z.odesilatel?.jmeno} · {formatCas(z.created_at)}</div>
                                <ZaskokKarta id={z.objekt.id} obnova={obnovaObjektu[z.id] || 0} isDark={isDark} onZmena={() => fetchZpravy()} />
                            </div>
                        );
                    }
                    const showUnreadDivider = firstUnreadId && z.id === firstUnreadId;
                    const isHovered = hoveredId === z.id;
                    const showActions = isHovered || menuPopup?.zpravaId === z.id || emojiPopup?.zpravaId === z.id;

                    const actionButtons = !z.smazano && (
                        <div className="flex items-center gap-0.5 self-start mt-2 shrink-0"
                            style={{ opacity: showActions ? 1 : 0, transition: 'opacity 0.15s', pointerEvents: showActions ? 'auto' : 'none' }}>
                            <button
                                onClick={(e) => openEmojiPicker(e, z.id)}
                                className="p-1 rounded-md transition-colors hover:opacity-100"
                                style={{ color: 'var(--text-muted)', opacity: 0.5 }}>
                                <Smile size={16} />
                            </button>
                            {(!isSdeleni || smiPripnout) && (
                                <button
                                    onClick={(e) => openMenu(e, z.id, isOwn)}
                                    className="p-1 rounded-md transition-colors hover:opacity-100"
                                    style={{ color: 'var(--text-muted)', opacity: 0.5 }}>
                                    <ChevronDown size={16} />
                                </button>
                            )}
                        </div>
                    );

                    return (
                        <div key={z.id}>
                        {showUnreadDivider && (
                            <div className="flex items-center gap-2 my-3 select-none">
                                <div className="flex-1 h-px" style={{ background: SECTION_BORDER }} />
                                <span className="text-xs font-medium px-2 py-0.5 rounded-full"
                                    style={{ color: SECTION_COLOR, background: SECTION_BG }}>
                                    Nové zprávy
                                </span>
                                <div className="flex-1 h-px" style={{ background: SECTION_BORDER }} />
                            </div>
                        )}
                        <div
                            className={`flex items-start gap-1 ${isSdeleni ? '' : (isOwn ? 'justify-end' : 'justify-start')}`}
                            onMouseEnter={() => setHoveredId(z.id)}
                            onMouseLeave={() => setHoveredId(null)}
                            style={{ position: 'relative', opacity: z._optimistic && !z._failed ? 0.6 : 1, transition: 'opacity 0.2s' }}>

                            {isOwn && !isSdeleni && actionButtons}

                            <div style={{ maxWidth: isSdeleni ? '100%' : '80%', width: isSdeleni ? '100%' : undefined }}>
                                {!isOwn && !isSdeleni && (
                                    <div className="text-xs mb-0.5 ml-2" style={{ color: 'var(--text-muted)' }}>{z.odesilatel?.jmeno}</div>
                                )}
                                {isSdeleni && (
                                    <div className="flex items-center gap-2 mb-1.5 ml-1">
                                        <ChatAvatar url={z.odesilatel?.avatar_url} jmeno={detail?.prezdivka || z.odesilatel?.jmeno} size={28} />
                                        <div>
                                            <div className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>{detail?.prezdivka || z.odesilatel?.jmeno}</div>
                                            <div className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{formatCas(z.created_at)}</div>
                                        </div>
                                    </div>
                                )}

                                {z.odpoved_na && !isSdeleni && (
                                    <div className="text-xs rounded-lg px-3 py-1.5 mb-1" style={{
                                        background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
                                        borderLeft: `3px solid ${SECTION_COLOR}`,
                                        color: 'var(--text-muted)',
                                    }}>
                                        <span className="font-medium">{z.odpoved_na.autor}</span>: {z.odpoved_na.obsah}
                                    </div>
                                )}

                                <div style={z.smazano ? { ...systemStyle, fontStyle: 'italic' } : (isSdeleni ? bubbleSdeleni : (isOwn ? bubbleOwn : bubbleOther))}>
                                    {z.smazano ? (
                                        'Zpráva byla smazána'
                                    ) : editingId === z.id ? (
                                        <div className="flex gap-2">
                                            <input value={editText} onChange={e => setEditText(e.target.value)}
                                                onKeyDown={e => e.key === 'Enter' && handleEdit(z.id)}
                                                className="flex-1 bg-transparent outline-none text-sm"
                                                style={{ color: 'inherit' }} autoFocus />
                                            <button onClick={() => handleEdit(z.id)} className="text-xs opacity-70">✓</button>
                                            <button onClick={() => setEditingId(null)} className="text-xs opacity-70">✕</button>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="text-sm whitespace-pre-wrap">{isSdeleni ? renderFormattedText(z.obsah) : renderTextWithLinks(z.obsah, isOwn, jmenaUcastniku)}</div>
                                            {z.soubory?.length > 0 && (
                                                <div className="mt-2 flex flex-col gap-1.5">
                                                    {z.soubory.map(s => {
                                                        const inlineUrl = s.url || `/api/chat/soubory/${s.id}/inline`;
                                                        const thumbUrl = s.nahled_url || inlineUrl;

                                                        if (s.je_obrazek) return (
                                                            <img key={s.id}
                                                                src={thumbUrl}
                                                                alt={s.nazev_original}
                                                                onClick={() => setLightbox({ url: inlineUrl, nazev: s.nazev_original, downloadId: s.id, type: 'image' })}
                                                                style={{
                                                                    maxWidth: 240, maxHeight: 200, borderRadius: 7,
                                                                    cursor: 'pointer', objectFit: 'cover',
                                                                    border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
                                                                }}
                                                            />
                                                        );

                                                        if (s.je_video || s.mime_typ?.startsWith('video/')) return (
                                                            <div key={s.id}
                                                                onClick={() => setLightbox({ url: inlineUrl, nazev: s.nazev_original, downloadId: s.id, type: 'video', mime: s.mime_typ })}
                                                                style={{
                                                                    position: 'relative', maxWidth: 260, borderRadius: 7, overflow: 'hidden',
                                                                    cursor: 'pointer',
                                                                    border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
                                                                    background: 'rgba(0,0,0,0.85)',
                                                                }}>
                                                                <video src={inlineUrl} muted playsInline preload="metadata"
                                                                    style={{ width: '100%', maxHeight: 180, objectFit: 'cover', display: 'block', opacity: 0.7 }}
                                                                    onLoadedData={e => { e.target.currentTime = 0.5; }}
                                                                />
                                                                <div style={{
                                                                    position: 'absolute', inset: 0,
                                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                }}>
                                                                    <div style={{
                                                                        width: 44, height: 44, borderRadius: '50%',
                                                                        background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)',
                                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                    }}>
                                                                        <Play size={20} fill="white" style={{ color: 'white', marginLeft: 2 }} />
                                                                    </div>
                                                                </div>
                                                                <div style={{
                                                                    position: 'absolute', bottom: 6, left: 8, right: 8,
                                                                    display: 'flex', alignItems: 'center', gap: 4,
                                                                    fontSize: 10, color: 'rgba(255,255,255,0.7)',
                                                                }}>
                                                                    <Film size={10} />
                                                                    <span className="truncate">{s.nazev_original}</span>
                                                                    <span style={{ marginLeft: 'auto', flexShrink: 0 }}>{formatVelikost(s.velikost_b)}</span>
                                                                </div>
                                                            </div>
                                                        );

                                                        // Ostatní soubory — kartička
                                                        return (
                                                            <a key={s.id} href={`/api/chat/soubory/${s.id}`}
                                                                target="_blank" rel="noopener"
                                                                style={{
                                                                    display: 'flex', alignItems: 'center', gap: 10,
                                                                    padding: '8px 12px', borderRadius: 7, textDecoration: 'none',
                                                                    background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                                                                    border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
                                                                    maxWidth: 260,
                                                                }}>
                                                                <div style={{
                                                                    width: 36, height: 36, borderRadius: 6, flexShrink: 0,
                                                                    background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
                                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                }}>
                                                                    {fileIcon(s.mime_typ)}
                                                                </div>
                                                                <div style={{ minWidth: 0, flex: 1 }}>
                                                                    <div className="truncate" style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-primary)' }}>
                                                                        {s.nazev_original}
                                                                    </div>
                                                                    <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                                                                        {formatVelikost(s.velikost_b)}
                                                                    </div>
                                                                </div>
                                                                <Download size={14} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                                                            </a>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>

                                {z.reakce?.length > 0 && (
                                    <div className="flex flex-wrap gap-1 mt-1">
                                        {z.reakce.map(r => (
                                            <button key={r.emoji} onClick={() => handleReakce(z.id, r.emoji)}
                                                className="text-xs rounded-full px-2 py-0.5 transition-colors"
                                                style={{
                                                    background: r.uzivatel_ids?.includes(user?.id) ? SECTION_BG : (isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)'),
                                                    border: `1px solid ${r.uzivatel_ids?.includes(user?.id) ? SECTION_BORDER : (isDark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)')}`,
                                                    color: 'var(--text-primary)',
                                                }}>
                                                {r.emoji} {r.pocet > 1 && r.pocet}
                                            </button>
                                        ))}
                                    </div>
                                )}

                                {/* Stav selhání — retry tlačítko */}
                                {z._failed && (
                                    <div className="flex items-center gap-2 mt-1" style={{ fontSize: 11 }}>
                                        <AlertCircle size={12} style={{ color: '#EF4444' }} />
                                        <span style={{ color: '#EF4444' }}>Nepodařilo se odeslat</span>
                                        <button onClick={() => handleRetry(z)}
                                            className="flex items-center gap-1 px-2 py-0.5 rounded-md transition-colors"
                                            style={{ color: SECTION_COLOR, background: SECTION_BG, fontSize: 11, fontWeight: 500 }}>
                                            <RotateCcw size={10} /> Zkusit znovu
                                        </button>
                                        <button onClick={() => setZpravy(prev => prev.filter(m => m.id !== z.id))}
                                            style={{ color: 'var(--text-muted)', fontSize: 11 }}>
                                            Zahodit
                                        </button>
                                    </div>
                                )}

                                <div className={`flex items-center gap-1 mt-0.5 ${isOwn && !isSdeleni ? 'justify-end' : ''}`}
                                    style={{ color: isDark ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.35)', fontSize: '11px' }}>
                                    {!isSdeleni && formatCas(z.created_at)}
                                    {z._optimistic && !z._failed && <Loader2 size={10} className="animate-spin" style={{ color: 'var(--text-muted)' }} />}
                                    {z.upraveno && !z.smazano && <span>(upraveno)</span>}
                                    {z.pripnuto && !z.smazano && <Pin size={10} style={{ color: SECTION_COLOR }} aria-label="Připnuto" />}
                                    {isOwn && !z.smazano && !z._optimistic && stavDoruceni(z)}
                                </div>
                            </div>

                            {(!isOwn || isSdeleni) && actionButtons}
                        </div>
                        </div>
                    );
                })}
                <div ref={messagesEndRef} />
            </div>

            {/* Psací panel — plovoucí kapsle, ne pruh přilepený ke spodní hraně.
                Stejný jazyk jako mobilní lišta: odsazení od hran, mléčné sklo,
                zaoblení, bez linky navrch. Sponka, pole i odeslání jsou uvnitř
                jednoho rámu, takže to je jeden ovladač, ne tři prvky vedle sebe. */}
            {canWrite && (
                <div ref={psaciRef} style={{
                    // Mimo tok: zprávy pod kapslí rolují dál, jak má plovoucí
                    // prvek dělat. V toku o ni končily ostrou hranou.
                    position: 'absolute', left: 0, right: 0, bottom: 0, zIndex: 3,
                    // Boční odstup i odsazení zdola přesně jako ovládací lišta —
                    // je to plovoucí kapsle u téže hrany, jen v jiném modulu.
                    padding: `0 ${pole.okraj}px`,
                    paddingBottom: kbOffset > 0
                        ? kbOffset + 12
                        : pole.zdola,
                    transition: 'padding-bottom 0.1s ease-out',
                }}>
                    <div style={{
                        // Výška i zaoblení pásu lišty. Při delší zprávě kapsle
                        // roste nahoru, spodní hrana zůstává na svém.
                        minHeight: pole.vyska,
                        borderRadius: pole.vyska / 2,
                        padding: jeMobil && !compact ? 5 : 4,
                        // Sloupec: nad řádkem psaní se skládá citace, přílohy,
                        // „píše…" a nabídka zmínek — v řádku stály vedle pole.
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'stretch',
                        /* Krytí musí být vyšší než u lišty: panel sedí v levém
                           dolním rohu, kam kotví oranžová část brandové záře —
                           při 7 % se přes něj propsala a kapsle vyšla zlatá,
                           včetně tlačítka odeslat, které v ní zmizelo. */
                        /* Materiál je **totožný s ovládací lištou** — stejné
                           krytí, stejné odlesky, stejné rozostření, bez rámečku.
                           Dvě plovoucí kapsle nad spodní hranou nemají vypadat
                           každá z jiného skla. Hodnoty žijí v `MobilniLista`
                           (`sklo`) a v tokenu `ROZOSTRENI_NAVIGACE`. */
                        ...(isDark
                            ? {
                                background: 'rgba(255,255,255,0.07)',
                                boxShadow: '0 12px 36px rgba(0,0,0,0.44), inset 0 1px 0 rgba(255,255,255,0.30), inset 0 -8px 20px rgba(255,255,255,0.05)',
                            }
                            : {
                                background: 'rgba(255,255,255,0.55)',
                                boxShadow: '0 12px 32px rgba(30,30,50,0.18), inset 0 1px 0 rgba(255,255,255,0.95), inset 0 -8px 20px rgba(255,255,255,0.35)',
                            }),
                        border: 'none',
                        backdropFilter: ROZOSTRENI_NAVIGACE,
                        WebkitBackdropFilter: ROZOSTRENI_NAVIGACE,
                    }}>
                    {replyTo && (
                        <div className="flex items-center gap-2 mb-2 px-3 py-2 rounded-lg text-xs"
                            style={{ background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)', borderLeft: `3px solid ${SECTION_COLOR}` }}>
                            <Reply size={12} />
                            <span className="flex-1 truncate" style={{ color: 'var(--text-muted)' }}>
                                <span className="font-medium">{replyTo.odesilatel?.jmeno}</span>: {replyTo.obsah}
                            </span>
                            <button onClick={() => setReplyTo(null)}><X size={14} /></button>
                        </div>
                    )}

                    {pendingSoubory.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-2">
                            {pendingSoubory.map(s => (
                                <div key={s.id} className="flex items-center gap-1 text-xs px-2 py-1 rounded-lg"
                                    style={{ background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)', color: 'var(--text-secondary)' }}>
                                    {s.je_obrazek ? <ImageIcon size={12} /> : <FileText size={12} />}
                                    {s.nazev_original}
                                    <button onClick={() => setPendingSoubory(prev => prev.filter(p => p.id !== s.id))}><X size={12} /></button>
                                </div>
                            ))}
                        </div>
                    )}

                    {zminka !== null && nabidkaZminek.length > 0 && (
                        <div className="flex flex-col mb-2 rounded-xl overflow-hidden" style={{ border: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)'}` }}>
                            {nabidkaZminek.map(u => (
                                <button key={u.id} type="button" onMouseDown={(e) => { e.preventDefault(); vlozZminku(u); }}
                                    className="flex items-center gap-2 text-left text-sm"
                                    style={{ minHeight: 40, padding: '0 12px', background: 'transparent', border: 'none', color: 'var(--text-primary)', cursor: 'pointer' }}>
                                    <ChatAvatar url={u.avatar_url} jmeno={u.jmeno} size={24} />
                                    {u.jmeno}
                                </button>
                            ))}
                        </div>
                    )}

                    {typingUsers.length > 0 && (
                        <div className="flex items-center gap-1.5 px-1 pb-1" style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                            <span className="flex gap-0.5">
                                {[0,1,2].map(i => (
                                    <span key={i} className="w-1.5 h-1.5 rounded-full inline-block"
                                        style={{ background: SECTION_COLOR, animation: `bounce 1.2s ease-in-out ${i * 0.2}s infinite` }} />
                                ))}
                            </span>
                            <span>{typingUsers.map(u => u.jmeno).join(', ')} píše...</span>
                        </div>
                    )}

                    {/* Input row — flex items-end, send button stejně vysoký jako textarea */}
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, flex: 1, minWidth: 0 }}>
                        {/* „+" — příloha a věci, které mají tvar (žádost, záskok, předávka) */}
                        <button type="button" onClick={() => setMenuPridat(true)} aria-label="Přidat"
                            style={{
                                flexShrink: 0,
                                width: pole.tlacitko, height: pole.tlacitko,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                borderRadius: '50%',
                                color: 'var(--text-muted)',
                                background: 'transparent',
                            }}
                            disabled={uploading}>
                            {uploading ? <Loader2 size={pole.ikona} className="animate-spin" /> : <Plus size={pole.ikona + 2} />}
                        </button>
                        <input ref={fileInputRef} type="file" multiple accept={PRIJIMANE} className="hidden" onChange={handleFileSelect} />
                        <input ref={fotakRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleFileSelect} />

                        <textarea ref={textareaRef}
                            value={text}
                            onFocus={markRead}
                            enterKeyHint="send"
                            onChange={e => {
                                setText(e.target.value);
                                // „@xy" těsně před kurzorem → nabídka účastníků ke zmínce
                                const predKurzorem = e.target.value.slice(0, e.target.selectionStart);
                                const m = detail?.typ !== 'soukromy' ? predKurzorem.match(/(?:^|\s)@([^\s@]{0,20})$/) : null;
                                setZminka(m ? m[1] : null);
                                e.target.style.height = 'auto';
                                e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
                                const ted = Date.now();
                                if (window.Echo && konverzaceId && e.target.value.length > 0 && ted - posledniPiseRef.current > PISE_INTERVAL_MS) {
                                    posledniPiseRef.current = ted;
                                    window.Echo.private(`konverzace.${konverzaceId}`)
                                        .whisper('typing', { id: user?.id, jmeno: user?.jmeno });
                                }
                            }}
                            onKeyDown={handleKeyDown}
                            placeholder="Napište zprávu..."
                            rows={1}
                            style={{
                                flex: 1,
                                resize: 'none',
                                outline: 'none',
                                // Uvnitř kapsle: bez vlastního podkladu i rámečku,
                                // jinak by to byl rám v rámu.
                                background: 'transparent',
                                border: 'none',
                                padding: `${Math.max(6, (pole.tlacitko - 20) / 2)}px 4px`,
                                color: 'var(--text-primary)',
                                maxHeight: '120px',
                                // 16 px je minimum, při kterém iOS nezoomuje pole —
                                // na počítači stačí 14 jako všude jinde.
                                fontSize: `${pole.pismo}px`,
                                lineHeight: '1.4',
                                display: 'block',
                            }}
                        />

                        <button onClick={handleSend}
                            style={{
                                flexShrink: 0,
                                width: pole.tlacitko, height: pole.tlacitko,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                borderRadius: '50%',
                                background: SECTION_COLOR,
                                color: 'white',
                                opacity: (isSending || (!text.trim() && pendingSoubory.length === 0)) ? 0.45 : 1,
                                transition: 'opacity 0.15s',
                                cursor: isSending ? 'not-allowed' : 'pointer',
                            }}
                            disabled={isSending || (!text.trim() && pendingSoubory.length === 0)}>
                            {isSending ? <Loader2 size={pole.ikona} className="animate-spin" /> : <Send size={pole.ikona} />}
                        </button>
                    </div>
                    </div>
                </div>
            )}

            {!canWrite && (
                <div className="px-4 py-3 text-center text-xs" style={{ color: 'var(--text-muted)', borderTop: '1px solid var(--border-default)' }}>
                    Toto sdělení je pouze pro čtení.
                </div>
            )}
        </div>
        {emojiPortal}
        {menuPortal}
        {menuPridat && (() => {
            const jeKanalSmeny = (detail?.kanal_klic || '').startsWith('smena.');
            const volby = [
                { id: 'vyfotit', Icon: Camera, nazev: 'Vyfotit', popis: 'Rovnou fotoaparátem', onClick: () => { setMenuPridat(false); fotakRef.current?.click(); } },
                { id: 'soubor', Icon: Paperclip, nazev: 'Fotky a soubory', popis: 'Víc najednou · PDF, Word, Excel, video do 10 MB', onClick: () => { setMenuPridat(false); fileInputRef.current?.click(); } },
                { id: 'zadost', Icon: Inbox, nazev: 'Žádost', popis: 'Volno, nemoc, výměna, záloha, nákup', onClick: () => { setMenuPridat(false); setNovaZadost(true); } },
                ...(user?.zamestnanec_id ? [{ id: 'zaskok', Icon: Repeat, nazev: 'Hledám záskok', popis: 'Nabídnout svou směnu kolegům', onClick: () => {
                    setMenuPridat(false); setMojeSmeny([]);
                    fetch(`/api/shifts/zamestnance/${user.zamestnanec_id}/budouci`).then(r => (r.ok ? r.json() : [])).then(d => setMojeSmeny(Array.isArray(d) ? d : [])).catch(() => setMojeSmeny([]));
                } }] : []),
                { id: 'hlaseni', Icon: Wrench, nazev: 'Hlášení', popis: 'Závada, auto, surovina, reklamace — s fotkou', onClick: () => { setMenuPridat(false); setNoveHlaseni(true); } },
                ...(detail?.typ !== 'soukromy' && detail?.typ !== 'sdeleni' ? [{ id: 'anketa', Icon: BarChart3, nazev: 'Anketa', popis: 'Otázka s možnostmi, hlasuje se klepnutím', onClick: () => { setMenuPridat(false); setNovaAnketa(true); } }] : []),
                ...(detail?.ucastnici?.length > 1 ? [{ id: 'podekovat', Icon: PartyPopper, nazev: 'Poděkovat kolegovi', popis: 'Díky do kanálu Všichni — ať to vidí celý tým', onClick: () => { setMenuPridat(false); setPodekovat(true); } }] : []),
                ...(jeKanalSmeny ? [{ id: 'predavka', Icon: ClipboardList, nazev: 'Předávka směny', popis: 'Co došlo, co je rozdělané — připne se i zítřejší směně', onClick: () => { setMenuPridat(false); setPredavka(''); } }] : []),
            ];
            return (
                <ModalShell onClose={() => setMenuPridat(false)} isDark={isDark} nadpis="Přidat" ikona={Plus} barva={SECTION_COLOR} sirka={420}>
                    <div style={{ padding: '8px 12px 12px' }}>
                        {volby.map(v => (
                            <button key={v.id} type="button" onClick={v.onClick} className="w-full flex items-center text-left rounded-xl"
                                style={{ gap: 12, minHeight: 58, padding: '8px 10px', background: 'transparent', border: 'none', cursor: 'pointer' }}>
                                <span style={{ width: 38, height: 38, borderRadius: 10, background: SECTION_BG, color: SECTION_COLOR, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><v.Icon size={18} /></span>
                                <span>
                                    <span style={{ display: 'block', fontWeight: 700, color: 'var(--text-primary)' }}>{v.nazev}</span>
                                    <span style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)' }}>{v.popis}</span>
                                </span>
                            </button>
                        ))}
                    </div>
                </ModalShell>
            );
        })()}
        {novaZadost && <NovaZadost user={user} isDark={isDark} onClose={() => setNovaZadost(false)} onHotovo={() => setNovaZadost(false)} />}
        {noveHlaseni && <NovyUkol rezim="hlaseni" isDark={isDark} konverzaceId={konverzaceId} onClose={() => setNoveHlaseni(false)} onHotovo={() => { setNoveHlaseni(false); fetchZpravy(); onKonverzaceZmenena?.(); }} />}
        {ukolZeZpravy && <NovyUkol rezim="ukol" isDark={isDark} konverzaceId={konverzaceId} zdrojZprava={ukolZeZpravy}
            lide={(detail?.ucastnici || []).map(u => ({ id: u.id, jmeno: u.id === user?.id ? `${u.jmeno} (já)` : u.jmeno }))}
            onClose={() => setUkolZeZpravy(null)} onHotovo={() => { setUkolZeZpravy(null); fetchZpravy(); onKonverzaceZmenena?.(); }} />}
        {podekovat && <Podekovat isDark={isDark} lide={(detail?.ucastnici || []).filter(u => u.id !== user?.id)} onClose={() => setPodekovat(false)} onHotovo={() => setPodekovat(false)} />}
        {novaAnketa && <NovaAnketa konverzaceId={konverzaceId} isDark={isDark} onClose={() => setNovaAnketa(false)} onHotovo={() => { setNovaAnketa(false); fetchZpravy(); onKonverzaceZmenena?.(); }} />}
        {mojeSmeny !== null && (
            <ModalShell onClose={() => { setMojeSmeny(null); setHlaskaPridat(null); }} isDark={isDark} nadpis="Hledám záskok" ikona={Repeat} barva={SECTION_COLOR} sirka={440}>
                <div style={{ padding: '8px 16px 16px' }}>
                    <p className="text-sm" style={{ color: 'var(--text-secondary)', margin: '4px 0 10px' }}>Vyber směnu — nabídne se kolegům s volnem a kvalifikací. Kdo klepne „Beru", toho potvrdí vedoucí.</p>
                    {mojeSmeny.length === 0 && <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Žádná směna v příštích 30 dnech (musí začínat nejdřív za 12 hodin).</p>}
                    {mojeSmeny.map(s => (
                        <button key={s.id} type="button" className="w-full text-left rounded-lg"
                            style={{ minHeight: 50, padding: '6px 12px', marginBottom: 6, background: 'transparent', border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'}`, cursor: 'pointer' }}
                            onClick={async () => {
                                const res = await fetch('/api/zaskoky', { method: 'POST', headers: hlavicky(), body: JSON.stringify({ smena_id: s.id }) });
                                const d = await res.json().catch(() => ({}));
                                if (res.ok) { setMojeSmeny(null); setHlaskaPridat(null); onKonverzaceZmenena?.(); }
                                else setHlaskaPridat(d.error || 'Nepodařilo se.');
                            }}>
                            <span style={{ display: 'block', fontWeight: 600, color: 'var(--text-primary)' }}>{new Date(s.datum).toLocaleDateString('cs', { weekday: 'long', day: 'numeric', month: 'numeric' })}</span>
                            <span style={{ display: 'block', fontSize: 12.5, color: 'var(--text-muted)' }}>{String(s.cas_od).slice(0, 5)}–{String(s.cas_do).slice(0, 5)}{s.sablon?.nazev ? ` · ${s.sablon.nazev}` : ''}</span>
                        </button>
                    ))}
                    {hlaskaPridat && <p className="text-xs" style={{ color: '#F87171' }}>{hlaskaPridat}</p>}
                </div>
            </ModalShell>
        )}
        {predavka !== null && (
            <ModalShell onClose={() => { setPredavka(null); setHlaskaPridat(null); }} isDark={isDark} nadpis="Předávka směny" ikona={ClipboardList} barva={SECTION_COLOR} sirka={480}
                paticka={(
                    <div className="flex items-center gap-2">
                        {hlaskaPridat && <span className="text-xs flex-1" style={{ color: '#F87171' }}>{hlaskaPridat}</span>}
                        <button type="button" disabled={!predavka.trim()} className="rounded-lg font-semibold ml-auto"
                            style={{ height: 42, padding: '0 18px', background: SECTION_COLOR, color: '#fff', border: 'none', cursor: predavka.trim() ? 'pointer' : 'not-allowed', opacity: predavka.trim() ? 1 : 0.45 }}
                            onClick={async () => {
                                const res = await fetch('/api/chat/predavka', { method: 'POST', headers: hlavicky(), body: JSON.stringify({ text: predavka }) });
                                const d = await res.json().catch(() => ({}));
                                if (res.ok) { setPredavka(null); setHlaskaPridat(null); setObnovaInterni(n => n + 1); } else setHlaskaPridat(d.error || 'Nepodařilo se.');
                            }}>Odeslat předávku</button>
                    </div>
                )}>
                <div style={{ padding: '8px 16px 16px' }}>
                    <textarea value={predavka} onChange={e => setPredavka(e.target.value)} rows={6} autoFocus
                        placeholder={'Co došlo, co je rozdělané, na co si dát pozor.\nNapř. došla mozzarella, objednáno na zítra; myčka teče.'}
                        style={{ width: '100%', borderRadius: 8, padding: '10px 12px', outline: 'none', fontSize: 15, lineHeight: 1.5, background: isDark ? 'rgba(0,0,0,0.35)' : 'var(--surface-raised)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'}`, color: 'var(--text-primary)' }} />
                    <p className="text-xs" style={{ color: 'var(--text-muted)', marginTop: 6 }}>Připne se v kanálu dnešní i zítřejší směny.</p>
                </div>
            </ModalShell>
        )}
        {precteniZpravy && (() => {
            const ostatni = (detail?.ucastnici || []).filter(u => u.id !== precteniZpravy.odesilatel_id);
            const precetli = ostatni.filter(u => (precteniZpravy.precteno_kym || []).includes(u.id));
            const neprecetli = ostatni.filter(u => !(precteniZpravy.precteno_kym || []).includes(u.id));
            const radek = (u) => (
                <div key={u.id} className="flex items-center" style={{ gap: 10, minHeight: 44, padding: '0 16px', borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'var(--border-subtle)'}` }}>
                    <ChatAvatar url={u.avatar_url} jmeno={u.jmeno} size={28} />
                    <span className="text-sm" style={{ color: 'var(--text-primary)' }}>{u.jmeno}</span>
                </div>
            );
            const nadpis = (t) => <p className="text-[11px] font-bold uppercase" style={{ color: 'var(--text-muted)', letterSpacing: '0.05em', padding: '14px 16px 6px', margin: 0 }}>{t}</p>;
            return (
                <ModalShell onClose={() => setPrecteniZpravy(null)} isDark={isDark} nadpis={`Přečetlo ${precetli.length} z ${ostatni.length}`} ikona={Eye} barva={SECTION_COLOR} sirka={420}>
                    {nadpis(`Přečetli · ${precetli.length}`)}
                    {precetli.map(radek)}
                    {neprecetli.length > 0 && nadpis(`Zatím ne · ${neprecetli.length}`)}
                    {neprecetli.map(radek)}
                </ModalShell>
            );
        })()}
        {lightbox && createPortal(
            <div onClick={() => setLightbox(null)} style={{
                position: 'fixed', inset: 0, zIndex: Z_NAD_LISTOU + 40,
                background: 'rgba(0,0,0,0.90)',
                backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                cursor: 'zoom-out',
            }}>
                {lightbox.type === 'video' ? (
                    <video src={lightbox.url} controls autoPlay playsInline
                        onClick={e => e.stopPropagation()}
                        style={{
                            maxWidth: '90vw', maxHeight: '85vh', borderRadius: 6,
                            cursor: 'default', boxShadow: '0 8px 40px rgba(0,0,0,0.5)',
                            background: 'black',
                        }} />
                ) : (
                    <img src={lightbox.url} alt={lightbox.nazev}
                        onClick={e => e.stopPropagation()}
                        style={{
                            maxWidth: '90vw', maxHeight: '85vh', objectFit: 'contain',
                            borderRadius: 6, cursor: 'default',
                            boxShadow: '0 8px 40px rgba(0,0,0,0.5)',
                        }} />
                )}
                <div style={{
                    display: 'flex', alignItems: 'center', gap: 12, marginTop: 16,
                }}>
                    <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: 13 }}>{lightbox.nazev}</span>
                    <a href={`/api/chat/soubory/${lightbox.downloadId}`}
                        onClick={e => e.stopPropagation()}
                        style={{
                            display: 'flex', alignItems: 'center', gap: 4,
                            padding: '6px 14px', borderRadius: 6,
                            background: 'rgba(255,255,255,0.12)',
                            color: 'white', fontSize: 12, fontWeight: 500,
                            textDecoration: 'none',
                        }}>
                        <Download size={13} /> Stáhnout
                    </a>
                    <button onClick={() => setLightbox(null)} style={{
                        width: 32, height: 32, borderRadius: 6,
                        background: 'rgba(255,255,255,0.12)',
                        color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        border: 'none', cursor: 'pointer',
                    }}>
                        <X size={16} />
                    </button>
                </div>
            </div>,
            document.body
        )}
        </>
    );
}
